import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AppConfig } from '../app-config.loader';
import { DistruzioneRequest } from '../shared/models/DistruzioneRequest.model';
import { Acquisizione } from '../shared/models/acquisizione.model';
import { Declassifica } from '../shared/models/declassifica.model';
import { DocumentiFTSearchFilters, DocumentiFTSearchRequest, DocumentiFTSearchResponse, DocumentiSearchFilters, DocumentiSearchRequest, DocumentiSearchResponse, DocumentiSearchResultRow } from '../shared/models/documento-search.model';
import { Documento } from '../shared/models/documento.model';
import { DocumentoRiferimento } from '../shared/models/documentoRiferimento.model';
import { DocumentoRiproduzione, DocumentoRiproduzioneRow } from '../shared/models/documentoRiproduzione.model';
import { Etichetta } from '../shared/models/etichetta.model';
import { Indirizzo } from '../shared/models/indirizzo.model';
import { NewStateRequest } from '../shared/models/newStateRequest.model';
import { Posizione } from '../shared/models/posizione.model';
import { RevocaRequest } from '../shared/models/revocaRequest.model';
import { RicevutaTrasmissioneRequest } from '../shared/models/ricevutaTrasmissioneRequest.model';
import { StoricoStatoDocumento } from '../shared/models/storicoStatoDocumento.model';
import { Versamento } from '../shared/models/versamento.model';
import { DocumentiRecenti } from '../shared/models/documentiRecenti.model';
import { environment } from 'src/environments/environment';
import { StatiPredefiniti } from '../shared/models/stati-predefiniti';

@Injectable({
  providedIn: 'root'
})
export class DocumentiService {
  private apiBaseUrl: string;
  private lastFTSearchResponse: DocumentiFTSearchResponse | null = null;
  private lastFTSearchRequest: DocumentiFTSearchRequest | null = null;
  private lastSearchResponse: DocumentiSearchResponse | null = null;
  private lastSearchRequest: DocumentiSearchRequest | null = null;

  constructor(appConfig: AppConfig, private http: HttpClient) {
    this.apiBaseUrl = `${appConfig.settings.apiRoot}`;
  }

  public getPosizione(id: number): Observable<Posizione[]> {
    return this.http.get<Posizione[]>(`${this.apiBaseUrl}/documenti/${id}/posizione`);
  }

  public getDocumento(id: number): Observable<Documento> {
    return this.http.get<Documento>(`${this.apiBaseUrl}/documenti/${id}`)
      .pipe(map(d => (
        {
          ...d,
          dataDocumento: d.dataDocumento ? new Date(d.dataDocumento) : null,
          dataProtocollazione: d.dataProtocollazione ? new Date(d.dataProtocollazione) : null,
          dataInvio: d.dataInvio ? new Date(d.dataInvio) : null,
          dataScadenza: d.dataScadenza ? new Date(d.dataScadenza) : null,
          storicoStatiDocumento: d.storicoStatiDocumento ? d.storicoStatiDocumento.map(s => ({ ...s, timeStamp: new Date(s.timeStamp) })) : [],
        } as Documento)));
  }

  public getStorico(id: number): Observable<StoricoStatoDocumento[]> {
    return this.http.get<StoricoStatoDocumento[]>(`${this.apiBaseUrl}/documenti/${id}/storico`).pipe(
      map(response => response.map(s => ({ ...s, timeStamp: new Date(s.timeStamp) })))
    );
  }

  public search(request: DocumentiSearchRequest, useCache = false): Observable<DocumentiSearchResponse> {
    const localRequest = { ...request, invalidateCache: false };

    if (!localRequest.filters) {
      localRequest.filters = {} as DocumentiSearchFilters;
    }

    if (useCache && !request.invalidateCache && this.lastSearchRequest && this.lastSearchResponse && this.objectsAreEqual(localRequest, this.lastSearchRequest)) {
      return of(this.lastSearchResponse);
    }

    return this.http
      .post<DocumentiSearchResponse>(`${this.apiBaseUrl}/documenti/search`, request)
      .pipe(
        map(r => ({
          ...r,
          results: r.results.map(row => this.prepareDocumentiSearchResultRow(row))
        })
        ),
        tap(response => {
          if (useCache) {
            this.lastSearchResponse = response;
            this.lastSearchRequest = localRequest;
          }
        })
      );
  }
 
  public resetCache(): void {
    this.lastSearchResponse = null;
    this.lastSearchRequest = null;
  }

  public getCachedSearchRequest(): DocumentiSearchRequest | null {
    return this.lastSearchRequest ? this.lastSearchRequest : null;
  }

  public searchFullText(request: DocumentiFTSearchRequest, useCache = false): Observable<DocumentiFTSearchResponse> {
    if (!request.filters) {
      request.filters = {} as DocumentiFTSearchFilters;
    }

    if (useCache && this.lastFTSearchRequest && this.lastFTSearchResponse && this.objectsAreEqual(request, this.lastFTSearchRequest)) {
      return of(this.lastFTSearchResponse);
    }

    return this.http.post<DocumentiFTSearchResponse>(`${this.apiBaseUrl}/documenti/searchFullText`, request)
      .pipe(
        tap(response => {
          this.lastFTSearchResponse = response;
          this.lastFTSearchRequest = { ...request };
        })
      );
  }

  public resetFTCache(): void {
    this.lastFTSearchRequest = null;
    this.lastFTSearchResponse = null;
  }

  public getCachedFTSearchRequest(): DocumentiFTSearchRequest | null {
    return this.lastFTSearchRequest ? this.lastFTSearchRequest : null;
  }

  public getRiferimenti(documentId: number): Observable<DocumentoRiferimento[]> {
    return this.http.get<DocumentoRiferimento[]>(`${this.apiBaseUrl}/documenti/${documentId}/riferimenti`);
  }

  public addRiferimento(documentId: number, linkedDocumentId: number): Observable<unknown> {
    return this.http.post(`${this.apiBaseUrl}/documenti/${documentId}/riferimenti`, linkedDocumentId);
  }

  public deleteRiferimento(documentId: number, linkedDocumentId: number): Observable<unknown> {
    return this.http.delete(`${this.apiBaseUrl}/documenti/${documentId}/riferimenti/${linkedDocumentId}`);
  }

  public nuovoDocumento(documento: Documento): Observable<Documento> {
    return this.http.post<Documento>(`${this.apiBaseUrl}/documenti`, documento);
  }

  public trasmettiOriginali(docIdArrivo: number, documento: Documento): Observable<Documento> {
    return this.http.post<Documento>(`${this.apiBaseUrl}/documenti/${docIdArrivo}/trasmettioriginali`, documento);
  }

  public updateDocumento(documento: Documento): Observable<Documento> {
    return this.http.put<Documento>(`${this.apiBaseUrl}/documenti/${documento.documentoId}`, documento);
  }

  public getAllDocuments(): Observable<Documento[]> {
    return this.http.get<Documento[]>(`${this.apiBaseUrl}/documenti`);
  }

  public getCopie(documentId: number): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiBaseUrl}/documenti/${documentId}/copie`);
  }

  public unlock(documentId: number): Observable<unknown> {
    return this.http.put(`${this.apiBaseUrl}/documenti/${documentId}/sblocca`, null);
  }

  public lock(documentId: number): Observable<unknown> {
    return this.http.put(`${this.apiBaseUrl}/documenti/${documentId}/blocca`, null);
  }

  public changeState(documentId: number, newStatusId: number, utente: string, note: string, dataInvio?: Date, dataScadenza?: Date | null): Observable<number> {
    const request: NewStateRequest = {
      documentId,
      newStatusId,
      utente,
      note,
      dataInizio: dataInvio,
      dataScadenza
    };
    return this.http.put<number>(`${this.apiBaseUrl}/documenti/${documentId}/newstate`, request);
  }

  public addCopiaPer(documentId: number, posizioneId: string): Observable<number> {
    const formData: FormData = new FormData();
    formData.append('posizione', posizioneId);
    return this.http.put<number>(`${this.apiBaseUrl}/documenti/copia/${documentId}`,
      formData);
  }

  public spostaFascicolo(documentId: number, posizioneId: string): Observable<number> {
    const formData: FormData = new FormData();
    formData.append('posizione', posizioneId);
    return this.http.put<number>(`${this.apiBaseUrl}/documenti/moveFascicolo/${documentId}`, formData);
  }


  public deleteCopiaPer(documentId: number, posizioneId: string): Observable<number> {
    const re = /\//ig;
    posizioneId = posizioneId.replace(re, '_');
    return this.http.delete<number>(`${this.apiBaseUrl}/documenti/${documentId}/deletecopia/${posizioneId}`);
  }

  public prorogaEvidenza(documentId: number, date: Date): Observable<unknown> {
    return this.http.put(`${this.apiBaseUrl}/documenti/${documentId}/proroga`, date);
  }

  public declassify(documentId: number, payload: Declassifica): Observable<unknown> {
    return this.http.put(`${this.apiBaseUrl}/documenti/${documentId}/declassifica`, payload);
  }

  public destroy(documentId: number, payload: DistruzioneRequest): Observable<unknown> {
    return this.http.put(`${this.apiBaseUrl}/documenti/${documentId}/distruggi`, payload);
  }

  public sendToStateArchive(documentId: number, payload: Versamento): Observable<unknown> {
    return this.http.put(`${this.apiBaseUrl}/documenti/${documentId}/archivioDiStato`, payload);
  }

  public temporarySend(documentId: number, payload: Acquisizione): Observable<unknown> {
    return this.http.put(`${this.apiBaseUrl}/documenti/${documentId}/versamentoTemporaneo`, payload);
  }

  public revoke(documentId: number, payload: RevocaRequest): Observable<unknown> {
    return this.http.put(`${this.apiBaseUrl}/documenti/${documentId}/revoca`, payload);
  }

  public getRiproduzioni(documentId: number): Observable<DocumentoRiproduzioneRow[]> {
    return this.http.get<DocumentoRiproduzioneRow[]>(`${this.apiBaseUrl}/documenti/${documentId}/riproduzioni`).pipe(
      map(response => {
        response.forEach(row => row.dataRichiesta = new Date(row.dataRichiesta));
        return response;
      }));
  }

  public getDestinatari(documentId: number): Observable<Indirizzo[]> {
    return this.http.get<Indirizzo[]>(`${this.apiBaseUrl}/documenti/${documentId}/destinatari`).pipe(
    );
  }

  public addRiproduzione(documentId: number, docRiproduzione: DocumentoRiproduzioneRow): Observable<DocumentoRiproduzione> {
    return this.http.post<DocumentoRiproduzione>(`${this.apiBaseUrl}/documenti/${documentId}/riproduzioni`, docRiproduzione);
  }

  public transmitRiproduzione(riproduzioneTrasmessa: Documento, documentoRiproduzione: DocumentoRiproduzioneRow): Observable<Documento> {
    return this.http.post<Documento>(`${this.apiBaseUrl}/documenti/trasmetti`, { riproduzioneTrasmessa, documentoRiproduzione });
  }

  public sendRiproduzione(documentoRiproduzioneid: number): Observable<Documento> {
    return this.http.post<Documento>(`${this.apiBaseUrl}/documenti/spedisci`, documentoRiproduzioneid);
  }

  public transmitOriginali(riproduzioneTrasmessa: Documento, documentoRiproduzione: DocumentoRiproduzioneRow): Observable<Documento> {
    return this.http.post<Documento>(`${this.apiBaseUrl}/documenti/trasmetti`, { riproduzioneTrasmessa, documentoRiproduzione });
  }

  public scaricaRicevutaTrasmissione(ricevutaRequest: RicevutaTrasmissioneRequest, documentId: number): void {

    this.http.post<Blob>(`${this.apiBaseUrl}/documenti/${documentId}/ricevuta`, ricevutaRequest, {
      responseType: 'blob' as 'json',
      observe: 'response',

    }).subscribe((res) => {
      if (res.body!.size <= 0) return;
      const documentType = 'application/pdf';

      const file = new File([res.body!], 'Ricevuta_' + documentId, { type: documentType })
      const url = window.URL.createObjectURL(file);

      window.open(url, '_blank', 'noreferrer');
    });

  }

  public modifyRiproduzione(docRiproduzione: DocumentoRiproduzioneRow): Observable<unknown> {
    return this.http.put(`${this.apiBaseUrl}/documenti/${docRiproduzione.documentoId}/riproduzioni`, docRiproduzione);
  }

  public getEtichetta(documentId: number): Observable<Etichetta> {
    return this.http.get<Etichetta>(`${this.apiBaseUrl}/documenti/${documentId}/etichetta`);
  }

  public printLabel(documentId: number): void {
    window.open(`/stampa-etichetta/${documentId}`, '', 'popup,width=800,height=600,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no,left=400,top=200');
  }

  public scaricaSignXml(documentId: number): void {
    this.http.get<Blob>(`${this.apiBaseUrl}/Documenti/${documentId}/sign-xml`, {
      responseType: 'blob' as 'json',
      observe: 'response',
    }).subscribe(res => {
      if (!res.body) return;

      const header = res.headers.get('content-disposition') ?? res.headers.get('Content-Disposition');
      const fileName = this.resolveFileName(header, `documento_${documentId}_sign.xml`);
      const file = new File([res.body], fileName, { type: 'application/xml' });
      const url = window.URL.createObjectURL(file);

      const link = document.createElement('a');
      link.download = fileName;
      link.href = url;
      link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        link.remove();
      }, 100);
    });
  }

  public scaricaDocumentoAmministrativoXml(documentId: number): void {
    this.http.get<Blob>(`${this.apiBaseUrl}/Documenti/${documentId}/amministrativo-xml`, {
      responseType: 'blob' as 'json',
      observe: 'response',
    }).subscribe(res => {
      if (!res.body) return;

      const header = res.headers.get('content-disposition') ?? res.headers.get('Content-Disposition');
      const fileName = this.resolveFileName(header, `documento_${documentId}_dai.zip`);
      const file = new File([res.body], fileName, { type: 'application/zip' });
      const url = window.URL.createObjectURL(file);

      const link = document.createElement('a');
      link.download = fileName;
      link.href = url;
      link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        link.remove();
      }, 100);
    });
  }

  public reindexDocument(documentId: number): Observable<void> {
    return this.http.get<void>(`${this.apiBaseUrl}/documenti/${documentId}/reindex`);
  }

  public reindexAllDocuments(fromDocumentId: number, take: number): Observable<number[]> {
    return this.http.get<number[]>(`${this.apiBaseUrl}/documenti/reindexall?d=${fromDocumentId}&n=${take}`);
  }

  public getPermissions(type: string): Observable<string[]> {
    let params = new HttpParams();
    if (type) {
        params = params.append('type', type);
    }

    return this.http.get<string[]>(`${this.apiBaseUrl}/documenti/permessi`, { params: params });
  }

  public annullaListaOperazioni(documentId: number): Observable<number[]> {
    return this.http.get<number[]>(`${this.apiBaseUrl}/documenti/annulla/lista/${documentId}`);
  }

  public annullaOperazione(documentId: number, operation: StatiPredefiniti): Observable<number> {

    let method = '';

    if ( operation === StatiPredefiniti.Declassificato) {
      method = 'annulla/desclassifica';
    }

    if ( operation === StatiPredefiniti.Distrutto) {
      method = 'annulla/distruzione';
    }
    
    if ( method == '') {
      throw new RangeError("Operazione non consentita");
    }

    return this.http.put<number>(`${this.apiBaseUrl}/documenti/${method}/${documentId}`, null);
  }

  public annullaDistruzioneAllegatoCis(documentoId: number, numeriOrdine: number[]): Observable<unknown> {
    return this.http.put(`${this.apiBaseUrl}/documenti/annulla/distruzione/cis/${documentoId}`, numeriOrdine);
  }

  private prepareDocumentiSearchResultRow(row: DocumentiSearchResultRow): DocumentiSearchResultRow {
    if (row?.data?.type === 'D') {
      row.data.dataDocumento = new Date(row.data.dataDocumento);
      if (row.data.dataScadenza) {
        row.data.dataScadenza = new Date(row.data.dataScadenza);
      }
    } else if (row.children) {
      row.children = row.children.map(c => this.prepareDocumentiSearchResultRow(c));
    }

    return row;
  }

  private resolveFileName(contentDisposition: string | null, fallback: string): string {
    if (!contentDisposition) {
      return fallback;
    }

    const utf8Match = /filename\\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const asciiMatch = /filename=\"?([^\";]+)\"?/i.exec(contentDisposition);
    return asciiMatch?.[1] ?? fallback;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private objectsAreEqual(a: any, b: any): boolean {

    if (a === b) {
      return true;
    }

    if (a == null || b == null) {
      return false;
    }

    if (typeof a !== 'object' || typeof b !== 'object') {
      return false;
    }

    if ((a instanceof Date) && (b instanceof Date)) {
      const da = new Date(a);
      const db = new Date(b);

      if ( da != db) {
        return false;
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const keysA = Object.keys(a);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) {
      return false;
    }

    for (const key of keysA) {
      if (!keysB.includes(key)) {
        return false;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (typeof a[key] === 'function' || typeof b[key] === 'function') {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        if (a[key].toString() !== b[key].toString()) {
          return false;
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      } else if (!this.objectsAreEqual(a[key], b[key])) {
        return false;
      }
    }
    return true;
  }

  public UpdateDocumentoRecente(documentoId: number): Observable<number>{
    return this.http.put(`${this.apiBaseUrl}/DocumentiRecenti/update/${documentoId}?resultsToShow=${environment.topbarRecentDocuments.ResultsToShow}`, null, { responseType: 'text' }).pipe(map(response => parseInt(response)));
  }

  public getDocumentiRecentiByUtente(): Observable<DocumentiRecenti[]>{
    return this.http.get<DocumentiRecenti[]>(`${this.apiBaseUrl}/DocumentiRecenti/recenti?resultsToShow=${environment.topbarRecentDocuments.ResultsToShow}`);
  }
}
